import { gql } from '@apollo/client';

// ─── Products ────────────────────────────────────────────────────────────────

export const GET_PRODUCTS = gql`
  query GetProducts($includeInactive: Boolean) {
    products(includeInactive: $includeInactive) {
      id
      productId
      name
      icon
      category
      desc
      basePrice
      isActive
      updatedAt
      options {
        name
        label
        type
        choices {
          label
          extraCost
        }
        min
        max
        step
        unit
      }
    }
  }
`;

export const CREATE_PRODUCT = gql`
  mutation CreateProduct(
    $productId: String!
    $name: String!
    $icon: String
    $category: String
    $desc: String
    $basePrice: Float!
    $isActive: Boolean
    $options: [ProductOptionInput!]
  ) {
    createProduct(
      productId: $productId
      name: $name
      icon: $icon
      category: $category
      desc: $desc
      basePrice: $basePrice
      isActive: $isActive
      options: $options
    ) {
      success
      message
      product {
        id
        productId
        name
        icon
        category
        desc
        basePrice
        isActive
      }
    }
  }
`;

export const UPDATE_PRODUCT = gql`
  mutation UpdateProduct(
    $id: ID!
    $productId: String
    $name: String
    $icon: String
    $category: String
    $desc: String
    $basePrice: Float
    $isActive: Boolean
    $options: [ProductOptionInput!]
  ) {
    updateProduct(
      id: $id
      productId: $productId
      name: $name
      icon: $icon
      category: $category
      desc: $desc
      basePrice: $basePrice
      isActive: $isActive
      options: $options
    ) {
      success
      message
      product {
        id
        productId
        name
        icon
        category
        desc
        basePrice
        isActive
      }
    }
  }
`;

export const DELETE_PRODUCT = gql`
  mutation DeleteProduct($id: ID!) {
    deleteProduct(id: $id) {
      success
      message
    }
  }
`;

// ─── Orders ──────────────────────────────────────────────────────────────────

export const SUBMIT_ORDER = gql`
  mutation SubmitOrder($input: OrderInput!) {
    submitOrder(input: $input) {
      success
      message
      orderId
    }
  }
`;

export const ADMIN_CREATE_ORDER = gql`
  mutation AdminCreateOrder($input: OrderInput!) {
    submitOrder(input: $input) {
      success
      message
      orderId
    }
  }
`;

// ─── Contact ─────────────────────────────────────────────────────────────────

export const SUBMIT_CONTACT = gql`
  mutation SubmitContact($input: ContactInput!) {
    submitContact(input: $input) {
      success
      message
    }
  }
`;

// ─── Reviews ─────────────────────────────────────────────────────────────────

export const GET_REVIEWS = gql`
  query GetReviews {
    reviews(approvedOnly: true, limit: 10) {
      id
      name
      rating
      comment
      avatarColor
      createdAt
    }
  }
`;

export const SUBMIT_REVIEW = gql`
  mutation SubmitReview($input: ReviewInput!) {
    submitReview(input: $input) {
      success
      message
    }
  }
`;

// ─── Admin Auth & Dashboard ──────────────────────────────────────────────────

export const LOGIN = gql`
  mutation Login($username: String!, $password: String!) {
    login(username: $username, password: $password) {
      token
      user {
        id
        username
        customerId
        role
        gstNumber
        companyName
        companyAddress
        shippingAddress
      }
    }
  }
`;

export const GET_ADMIN_ORDERS = gql`
  query GetAdminOrders($status: String) {
    orders(status: $status) {
      id
      orderId
      items {
        productId
        productName
        quantity
        options {
          key
          value
        }
        designFileName
        estimatedPrice
      }
      totalAmount
      advanceAmount
      customerId
      customerName
      customerEmail
      customerPhone
      deliveryType
      deliveryAddress
      notes
      status
      razorpayOrderId
      razorpayPaymentId
      paymentStatus
      hasJobCard
      createdAt
    }
  }
`;

export const GET_ADMIN_CONTACTS = gql`
  query GetAdminContacts {
    contacts {
      id
      name
      email
      phone
      message
      read
      type
      createdAt
    }
  }
`;

export const GET_ADMIN_REVIEWS = gql`
  query GetAdminReviews {
    reviews(approvedOnly: false) {
      id
      name
      email
      rating
      comment
      approved
      avatarColor
      createdAt
    }
  }
`;

export const UPDATE_ORDER_STATUS = gql`
  mutation UpdateOrderStatus($orderId: String!, $status: String!) {
    updateOrderStatus(orderId: $orderId, status: $status) {
      success
      message
      orderId
    }
  }
`;

export const UPDATE_ORDER = gql`
  mutation UpdateOrder($input: UpdateOrderInput!) {
    updateOrder(input: $input) {
      success
      message
      orderId
    }
  }
`;

export const MARK_CONTACT_READ = gql`
  mutation MarkContactRead($id: ID!) {
    markContactRead(id: $id) {
      success
      message
    }
  }
`;

export const APPROVE_REVIEW = gql`
  mutation ApproveReview($id: ID!) {
    approveReview(id: $id) {
      success
      message
    }
  }
`;

export const REGISTER = gql`
  mutation Register(
    $username: String!
    $password: String!
    $gstNumber: String
    $companyName: String
    $companyAddress: String
    $shippingAddress: String
  ) {
    register(
      username: $username
      password: $password
      gstNumber: $gstNumber
      companyName: $companyName
      companyAddress: $companyAddress
      shippingAddress: $shippingAddress
    ) {
      token
      user {
        id
        username
        customerId
        role
        gstNumber
        companyName
        companyAddress
        shippingAddress
      }
    }
  }
`;

export const SUBMIT_QUOTE = gql`
  mutation SubmitQuote($input: QuoteInput!) {
    submitQuote(input: $input) {
      success
      message
      quoteId
    }
  }
`;

export const SAVE_ESTIMATION = gql`
  mutation SaveEstimation($input: EstimationInput!) {
    saveEstimation(input: $input) {
      success
      message
      estimation {
        id
        estimateNo
      }
    }
  }
`;

export const DELETE_ESTIMATION = gql`
  mutation DeleteEstimation($estimateNo: String!) {
    deleteEstimation(estimateNo: $estimateNo) {
      success
      message
    }
  }
`;

export const GET_ADMIN_QUOTES = gql`
  query GetAdminQuotes($status: String, $limit: Int, $offset: Int) {
    quotes(status: $status, limit: $limit, offset: $offset) {
      id
      quoteId
      customerId
      product
      quantity
      size
      description
      items {
        product
        quantity
        size
        description
      }
      name
      phone
      email
      status
      type
      createdAt
    }
  }
`;

export const GET_ESTIMATIONS = gql`
  query GetEstimations {
    estimations {
      id
      estimateNo
      date
      validUntil
      clientInfo {
        name
        customerId
        company
        phone
        email
        address
        gstin
      }
      items {
        id
        productName
        productId
        specifications
        quantity
        unitPrice
        totalPrice
        isCustom
        width
        height
      }
      financials {
        subtotal
        discountType
        discountValue
        discountAmount
        taxRate
        taxAmount
        shippingCharges
        grandTotal
        advanceAmount
        balanceAmount
      }
      terms
      privateNotes
      generatedBy
      createdAt
    }
  }
`;

export const UPDATE_QUOTE_STATUS = gql`
  mutation UpdateQuoteStatus($quoteId: String!, $status: String!) {
    updateQuoteStatus(quoteId: $quoteId, status: $status) {
      success
      message
      quoteId
    }
  }
`;

export const CREATE_RAZORPAY_ORDER = gql`
  mutation CreateRazorpayOrder($amount: Float!) {
    createRazorpayOrder(amount: $amount) {
      id
      amount
      currency
    }
  }
`;

export const VERIFY_RAZORPAY_PAYMENT = gql`
  mutation VerifyRazorpayPayment(
    $orderId: String!
    $paymentId: String!
    $signature: String!
    $pdpOrderId: String!
  ) {
    verifyRazorpayPayment(
      orderId: $orderId
      paymentId: $paymentId
      signature: $signature
      pdpOrderId: $pdpOrderId
    ) {
      success
      message
    }
  }
`;

export const GET_MY_ORDERS = gql`
  query GetMyOrders {
    myOrders {
      id
      orderId
      items {
        productId
        productName
        quantity
        options {
          key
          value
        }
        designFileName
        estimatedPrice
      }
      totalAmount
      customerId
      customerName
      customerEmail
      customerPhone
      deliveryType
      deliveryAddress
      notes
      status
      razorpayOrderId
      razorpayPaymentId
      paymentStatus
      hasJobCard
      jobCardStatus
      jobCardOperator
      jobCardMachine
      jobCardId
      createdAt
    }
  }
`;

export const GET_MY_PROFILE = gql`
  query GetMyProfile {
    myProfile {
      id
      username
      customerId
      role
      gstNumber
      companyName
      companyAddress
      shippingAddress
      createdAt
      updatedAt
      savedDesigns {
        id
        fileName
        friendlyName
        uploadedAt
      }
    }
  }
`;

export const UPDATE_MY_PROFILE = gql`
  mutation UpdateMyProfile(
    $companyName: String
    $gstNumber: String
    $companyAddress: String
    $shippingAddress: String
    $currentPassword: String
    $newPassword: String
  ) {
    updateMyProfile(
      companyName: $companyName
      gstNumber: $gstNumber
      companyAddress: $companyAddress
      shippingAddress: $shippingAddress
      currentPassword: $currentPassword
      newPassword: $newPassword
    ) {
      success
      message
      user {
        id
        username
        customerId
        role
        gstNumber
        companyName
        companyAddress
        shippingAddress
        updatedAt
      }
    }
  }
`;

export const GET_ADMIN_USERS = gql`
  query GetAdminUsers {
    users {
      id
      username
      customerId
      role
      email
      phone
      gstNumber
      companyName
      companyAddress
      shippingAddress
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_USER = gql`
  mutation CreateUser(
    $username: String!
    $password: String!
    $role: String!
    $email: String
    $phone: String
    $companyName: String
    $gstNumber: String
    $companyAddress: String
    $shippingAddress: String
  ) {
    createUser(
      username: $username
      password: $password
      role: $role
      email: $email
      phone: $phone
      companyName: $companyName
      gstNumber: $gstNumber
      companyAddress: $companyAddress
      shippingAddress: $shippingAddress
    ) {
      success
      message
      user {
        id
        username
        customerId
        role
        email
        phone
        gstNumber
        companyName
        companyAddress
        shippingAddress
        createdAt
      }
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser(
    $id: ID!
    $username: String
    $password: String
    $role: String
    $email: String
    $phone: String
    $companyName: String
    $gstNumber: String
    $companyAddress: String
    $shippingAddress: String
  ) {
    updateUser(
      id: $id
      username: $username
      password: $password
      role: $role
      email: $email
      phone: $phone
      companyName: $companyName
      gstNumber: $gstNumber
      companyAddress: $companyAddress
      shippingAddress: $shippingAddress
    ) {
      success
      message
      user {
        id
        username
        customerId
        role
        email
        phone
        gstNumber
        companyName
        companyAddress
        shippingAddress
        updatedAt
      }
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id) {
      success
      message
      user {
        id
        username
        customerId
        role
      }
    }
  }
`;

export const UPDATE_PRODUCT_PRICING = gql`
  mutation UpdateProductPricing($productId: String!, $basePrice: Float!, $options: [ProductOptionInput!]!) {
    updateProductPricing(productId: $productId, basePrice: $basePrice, options: $options) {
      success
      message
      product {
        productId
        name
        basePrice
        options {
          name
          label
          type
          choices {
            label
            extraCost
          }
          min
          max
          step
          unit
        }
      }
    }
  }
`;

export const GET_ADMIN_JOBCARDS = gql`
  query GetAdminJobCards($status: String, $priority: String) {
    jobCards(status: $status, priority: $priority) {
      id
      jobCardId
      customerId
      orderId
      customerName
      customerPhone
      status
      operator
      designer
      machine
      material
      finishingInstructions
      priority
      notes
      updatedBy
      items {
        productId
        productName
        quantity
        options {
          key
          value
        }
        designFileName
      }
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_JOBCARD = gql`
  mutation CreateJobCard(
    $orderId: String!
    $operator: String
    $designer: String
    $machine: String
    $material: String
    $finishingInstructions: String
    $priority: String
    $notes: String
  ) {
    createJobCard(
      orderId: $orderId
      operator: $operator
      designer: $designer
      machine: $machine
      material: $material
      finishingInstructions: $finishingInstructions
      priority: $priority
      notes: $notes
    ) {
      success
      message
      jobCard {
        id
        jobCardId
        orderId
        customerName
        status
        updatedBy
      }
    }
  }
`;

export const UPDATE_JOBCARD = gql`
  mutation UpdateJobCard(
    $id: ID!
    $status: String
    $operator: String
    $designer: String
    $machine: String
    $material: String
    $finishingInstructions: String
    $priority: String
    $notes: String
  ) {
    updateJobCard(
      id: $id
      status: $status
      operator: $operator
      designer: $designer
      machine: $machine
      material: $material
      finishingInstructions: $finishingInstructions
      priority: $priority
      notes: $notes
    ) {
      success
      message
      jobCard {
        id
        jobCardId
        status
        operator
        designer
        machine
        material
        finishingInstructions
        priority
        notes
        updatedBy
      }
    }
  }
`;

export const DELETE_JOBCARD = gql`
  mutation DeleteJobCard($id: ID!) {
    deleteJobCard(id: $id) {
      success
      message
      jobCard {
        id
        jobCardId
      }
    }
  }
`;

// ─── Inventory ──────────────────────────────────────────────────────────────

export const GET_INVENTORY_ITEMS = gql`
  query GetInventoryItems($category: String) {
    inventoryItems(category: $category) {
      id
      name
      category
      stock
      unit
      minStock
      supplier
      notes
      orderDate
      bannerType
      bannerSize
      overallLength
      rollLengthLimit
      createdAt
      updatedAt
    }
  }
`;

export const GET_INVENTORY_ITEM = gql`
  query GetInventoryItem($id: ID!) {
    inventoryItem(id: $id) {
      id
      name
      category
      stock
      unit
      minStock
      supplier
      notes
      orderDate
      bannerType
      bannerSize
      overallLength
      rollLengthLimit
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_INVENTORY_ITEM = gql`
  mutation CreateInventoryItem($input: InventoryInput!) {
    createInventoryItem(input: $input) {
      success
      message
      item {
        id
        name
        category
        stock
        unit
        minStock
        supplier
        notes
        orderDate
        bannerType
        bannerSize
        overallLength
        rollLengthLimit
        createdAt
        updatedAt
      }
    }
  }
`;

export const UPDATE_INVENTORY_ITEM = gql`
  mutation UpdateInventoryItem($id: ID!, $input: InventoryInput!) {
    updateInventoryItem(id: $id, input: $input) {
      success
      message
      item {
        id
        name
        category
        stock
        unit
        minStock
        supplier
        notes
        orderDate
        bannerType
        bannerSize
        overallLength
        rollLengthLimit
        createdAt
        updatedAt
      }
    }
  }
`;

export const DELETE_INVENTORY_ITEM = gql`
  mutation DeleteInventoryItem($id: ID!) {
    deleteInventoryItem(id: $id) {
      success
      message
    }
  }
`;

// ─── Admin Delete Operations ──────────────────────────────────────────────────

export const DELETE_ORDER = gql`
  mutation DeleteOrder($orderId: String!) {
    deleteOrder(orderId: $orderId) {
      success
    }
  }
`;

export const DELETE_CONTACT = gql`
  mutation DeleteContact($id: ID!) {
    deleteContact(id: $id) {
      success
    }
  }
`;

export const DELETE_REVIEW = gql`
  mutation DeleteReview($id: ID!) {
    deleteReview(id: $id) {
      success
    }
  }
`;

export const DELETE_QUOTE = gql`
  mutation DeleteQuote($quoteId: String!) {
    deleteQuote(quoteId: $quoteId) {
      success
    }
  }
`;

// ─── Attendance ─────────────────────────────────────────────────────────────

export const GET_STAFF_ATTENDANCE = gql`
  query GetStaffAttendance($date: String!) {
    staffAttendance(date: $date) {
      user {
        id
        username
        role
      }
      attendance {
        id
        userId
        date
        status
        checkIn
        checkOut
        location
        latitude
        longitude
        notes
      }
    }
  }
`;

export const GET_ALL_STAFF_ATTENDANCE_SUMMARY = gql`
  query GetAllStaffAttendanceSummary($startDate: String!, $endDate: String!) {
    allStaffAttendanceSummary(startDate: $startDate, endDate: $endDate) {
      user {
        id
        username
        role
      }
      summary {
        userId
        presentCount
        absentCount
        halfDayCount
        leaveCount
        records {
          id
          userId
          date
          status
          checkIn
          checkOut
          location
          latitude
          longitude
          notes
        }
      }
    }
  }
`;

export const GET_ATTENDANCE_SUMMARY = gql`
  query GetAttendanceSummary($userId: ID!, $startDate: String!, $endDate: String!) {
    attendanceSummary(userId: $userId, startDate: $startDate, endDate: $endDate) {
      userId
      presentCount
      absentCount
      halfDayCount
      leaveCount
      records {
        id
        userId
        date
        status
        checkIn
        checkOut
        location
        latitude
        longitude
        notes
      }
    }
  }
`;

export const RECORD_LOGIN_ATTENDANCE = gql`
  mutation RecordLoginAttendance($latitude: Float, $longitude: Float, $location: String) {
    recordLoginAttendance(latitude: $latitude, longitude: $longitude, location: $location) {
      success
      message
      record {
        id
        userId
        date
        status
        checkIn
        checkOut
        location
        latitude
        longitude
        notes
      }
    }
  }
`;

export const MARK_ATTENDANCE = gql`
  mutation MarkAttendance(
    $userId: ID!
    $date: String!
    $status: String!
    $checkIn: String
    $checkOut: String
    $location: String
    $latitude: Float
    $longitude: Float
    $notes: String
  ) {
    markAttendance(
      userId: $userId
      date: $date
      status: $status
      checkIn: $checkIn
      checkOut: $checkOut
      location: $location
      latitude: $latitude
      longitude: $longitude
      notes: $notes
    ) {
      success
      message
      record {
        id
        userId
        date
        status
        checkIn
        checkOut
        location
        latitude
        longitude
        notes
      }
    }
  }
`;

export const GET_PAYSLIPS = gql`
  query GetPayslips($userId: ID, $month: Int, $year: Int) {
    payslips(userId: $userId, month: $month, year: $year) {
      id
      userId
      user {
        id
        username
        role
      }
      month
      year
      baseSalary
      allowances
      deductions
      tax
      netSalary
      presentDays
      workingDays
      status
      paymentMode
      notes
      createdAt
    }
  }
`;

export const GENERATE_PAYSLIP = gql`
  mutation GeneratePayslip(
    $userId: ID!
    $month: Int!
    $year: Int!
    $baseSalary: Float!
    $allowances: Float
    $deductions: Float
    $tax: Float
    $presentDays: Float!
    $workingDays: Float!
    $status: String
    $paymentMode: String
    $notes: String
  ) {
    generatePayslip(
      userId: $userId
      month: $month
      year: $year
      baseSalary: $baseSalary
      allowances: $allowances
      deductions: $deductions
      tax: $tax
      presentDays: $presentDays
      workingDays: $workingDays
      status: $status
      paymentMode: $paymentMode
      notes: $notes
    ) {
      success
      message
      payslip {
        id
        userId
        user {
          id
          username
          role
        }
        month
        year
        baseSalary
        allowances
        deductions
        tax
        netSalary
        presentDays
        workingDays
        status
        paymentMode
        notes
        createdAt
      }
    }
  }
`;

export const DELETE_PAYSLIP = gql`
  mutation DeletePayslip($id: ID!) {
    deletePayslip(id: $id) {
      success
      message
    }
  }
`;

export const UPDATE_PAYSLIP_STATUS = gql`
  mutation UpdatePayslipStatus($id: ID!, $status: String!) {
    updatePayslipStatus(id: $id, status: $status) {
      success
      message
      payslip {
        id
        status
      }
    }
  }
`;



// --- INVOICE OPERATIONS --------------------------------------------------

export const GET_INVOICES = gql`
  query GetInvoices {
    invoices {
      id
      invoiceNo
      jobCardId
      orderId
      orderStatus
      date
      dueDate
      preparedBy
      paymentStatus
      paymentMode
      clientInfo {
        name
        customerId
        company
        phone
        email
        address
        gstin
      }
      items {
        id
        description
        quantity
        unitPrice
        totalPrice
        isCustom
        hsnCode
      }
      subtotal
      discountType
      discountVal
      discountAmount
      taxType
      taxRate
      taxAmount
      totalAmount
      amountPaid
      balanceDue
      notes
      terms
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_INVOICE = gql`
  mutation CreateInvoice($input: InvoiceInput!) {
    createInvoice(input: $input) {
      success
      message
      invoice {
        id
        invoiceNo
      }
    }
  }
`;

export const UPDATE_INVOICE = gql`
  mutation UpdateInvoice($invoiceNo: String!, $input: InvoiceInput!) {
    updateInvoice(invoiceNo: $invoiceNo, input: $input) {
      success
      message
    }
  }
`;

export const DELETE_INVOICE = gql`
  mutation DeleteInvoice($invoiceNo: String!) {
    deleteInvoice(invoiceNo: $invoiceNo) {
      success
      message
    }
  }
`;

export const GENERATE_INVOICE_FROM_JOBCARD = gql`
  mutation GenerateInvoiceFromJobCard($jobCardId: String!) {
    generateInvoiceFromJobCard(jobCardId: $jobCardId) {
      success
      message
      invoice {
        id
        invoiceNo
      }
    }
  }
`;

// ─── Expenses ────────────────────────────────────────────────────────────────

export const GET_EXPENSES = gql`
  query GetExpenses($startDate: String, $endDate: String) {
    expenses(startDate: $startDate, endDate: $endDate) {
      id
      description
      amount
      category
      date
      paymentStatus
      paymentMode
      createdBy {
        id
        username
      }
      createdAt
    }
  }
`;

export const CREATE_EXPENSE = gql`
  mutation CreateExpense($input: ExpenseInput!) {
    createExpense(input: $input) {
      success
      message
      expense {
        id
        description
        amount
        category
        date
        paymentStatus
        paymentMode
      }
    }
  }
`;

export const DELETE_EXPENSE = gql`
  mutation DeleteExpense($id: ID!) {
    deleteExpense(id: $id) {
      success
      message
    }
  }
`;

export const UPDATE_EXPENSE = gql`
  mutation UpdateExpense($id: ID!, $input: ExpenseInput!) {
    updateExpense(id: $id, input: $input) {
      success
      message
      expense {
        id
        description
        amount
        category
        date
        paymentStatus
        paymentMode
      }
    }
  }
`;

export const UPDATE_EXPENSE_STATUS = gql`
  mutation UpdateExpenseStatus($id: ID!, $paymentStatus: String, $paymentMode: String) {
    updateExpenseStatus(id: $id, paymentStatus: $paymentStatus, paymentMode: $paymentMode) {
      success
      message
      expense {
        id
        paymentStatus
        paymentMode
      }
    }
  }
`;

export const SAVE_DESIGN = gql`
  mutation SaveDesign($fileName: String!, $friendlyName: String!) {
    saveDesign(fileName: $fileName, friendlyName: $friendlyName) {
      success
      message
      user {
        id
      }
    }
  }
`;

export const DELETE_DESIGN = gql`
  mutation DeleteDesign($designId: ID!) {
    deleteDesign(designId: $designId) {
      success
      message
      user {
        id
      }
    }
  }
`;

// ─── Tasks ────────────────────────────────────────────────────────────────────

const TASK_FIELDS = gql`
  fragment TaskFields on Task {
    id
    taskId
    title
    description
    assignedTo
    assignedBy
    assignedToUser {
      id
      username
      role
    }
    assignedByUser {
      id
      username
    }
    priority
    status
    dueDate
    category
    notes
    createdAt
    updatedAt
  }
`;

export const GET_TASKS = gql`
  ${TASK_FIELDS}
  query GetTasks($status: String, $assignedTo: ID, $priority: String) {
    tasks(status: $status, assignedTo: $assignedTo, priority: $priority) {
      ...TaskFields
    }
  }
`;

export const GET_MY_TASKS = gql`
  ${TASK_FIELDS}
  query GetMyTasks {
    myTasks {
      ...TaskFields
    }
  }
`;

export const CREATE_TASK = gql`
  ${TASK_FIELDS}
  mutation CreateTask(
    $title: String!
    $description: String
    $assignedTo: ID!
    $priority: String
    $dueDate: String
    $category: String
    $notes: String
  ) {
    createTask(
      title: $title
      description: $description
      assignedTo: $assignedTo
      priority: $priority
      dueDate: $dueDate
      category: $category
      notes: $notes
    ) {
      success
      message
      task {
        ...TaskFields
      }
    }
  }
`;

export const UPDATE_TASK = gql`
  ${TASK_FIELDS}
  mutation UpdateTask(
    $id: ID!
    $title: String
    $description: String
    $assignedTo: ID
    $priority: String
    $status: String
    $dueDate: String
    $category: String
    $notes: String
  ) {
    updateTask(
      id: $id
      title: $title
      description: $description
      assignedTo: $assignedTo
      priority: $priority
      status: $status
      dueDate: $dueDate
      category: $category
      notes: $notes
    ) {
      success
      message
      task {
        ...TaskFields
      }
    }
  }
`;

export const DELETE_TASK = gql`
  mutation DeleteTask($id: ID!) {
    deleteTask(id: $id) {
      success
      message
    }
  }
`;

// ─── Purchases ────────────────────────────────────────────────────────────────

const PURCHASE_FIELDS = gql`
  fragment PurchaseFields on Purchase {
    id
    purchaseId
    supplierName
    supplierContact
    supplierGSTIN
    category
    items {
      name
      quantity
      unit
      unitPrice
      totalPrice
    }
    totalAmount
    paidAmount
    balanceDue
    paymentStatus
    paymentMode
    invoiceRef
    date
    notes
    createdAt
    updatedAt
  }
`;

export const GET_PURCHASES = gql`
  ${PURCHASE_FIELDS}
  query GetPurchases($startDate: String, $endDate: String, $status: String, $category: String) {
    purchases(startDate: $startDate, endDate: $endDate, status: $status, category: $category) {
      ...PurchaseFields
    }
  }
`;

export const CREATE_PURCHASE = gql`
  ${PURCHASE_FIELDS}
  mutation CreatePurchase($input: PurchaseInput!) {
    createPurchase(input: $input) {
      success
      message
      purchase {
        ...PurchaseFields
      }
    }
  }
`;

export const UPDATE_PURCHASE = gql`
  ${PURCHASE_FIELDS}
  mutation UpdatePurchase($id: ID!, $input: PurchaseInput!) {
    updatePurchase(id: $id, input: $input) {
      success
      message
      purchase {
        ...PurchaseFields
      }
    }
  }
`;

export const UPDATE_PURCHASE_STATUS = gql`
  ${PURCHASE_FIELDS}
  mutation UpdatePurchaseStatus($id: ID!, $paymentStatus: String!, $paymentMode: String, $paidAmount: Float) {
    updatePurchaseStatus(id: $id, paymentStatus: $paymentStatus, paymentMode: $paymentMode, paidAmount: $paidAmount) {
      success
      message
      purchase {
        ...PurchaseFields
      }
    }
  }
`;

export const DELETE_PURCHASE = gql`
  mutation DeletePurchase($id: ID!) {
    deletePurchase(id: $id) {
      success
      message
    }
  }
`;

// ─── Sales ────────────────────────────────────────────────────────────────────

const SALE_FIELDS = gql`
  fragment SaleFields on Sale {
    id
    saleId
    customerName
    customerContact
    customerEmail
    category
    items {
      name
      quantity
      unit
      unitPrice
      totalPrice
    }
    totalAmount
    paidAmount
    balanceDue
    paymentStatus
    paymentMode
    invoiceRef
    date
    notes
    source
    createdAt
    updatedAt
  }
`;

export const GET_SALES = gql`
  ${SALE_FIELDS}
  query GetSales($startDate: String, $endDate: String, $status: String, $category: String, $source: String) {
    sales(startDate: $startDate, endDate: $endDate, status: $status, category: $category, source: $source) {
      ...SaleFields
    }
  }
`;

export const CREATE_SALE = gql`
  ${SALE_FIELDS}
  mutation CreateSale($input: SaleInput!) {
    createSale(input: $input) {
      success
      message
      sale {
        ...SaleFields
      }
    }
  }
`;

export const UPDATE_SALE = gql`
  ${SALE_FIELDS}
  mutation UpdateSale($id: ID!, $input: SaleInput!) {
    updateSale(id: $id, input: $input) {
      success
      message
      sale {
        ...SaleFields
      }
    }
  }
`;

export const UPDATE_SALE_STATUS = gql`
  ${SALE_FIELDS}
  mutation UpdateSaleStatus($id: ID!, $paymentStatus: String!, $paymentMode: String, $paidAmount: Float) {
    updateSaleStatus(id: $id, paymentStatus: $paymentStatus, paymentMode: $paymentMode, paidAmount: $paidAmount) {
      success
      message
      sale {
        ...SaleFields
      }
    }
  }
`;

export const DELETE_SALE = gql`
  mutation DeleteSale($id: ID!) {
    deleteSale(id: $id) {
      success
      message
    }
  }
`;



